// Global Instructions Rule Applied!
import React, { useState, useEffect, useCallback, useMemo } from 'react'
import dayjs from 'dayjs'
import { Row, Col } from 'antd'
import { useTabManagerContext } from '../../../../../core/app-layout/lib/TabManagerContext.tsx'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faDownload } from '@fortawesome/free-solid-svg-icons'
import Button from '../../../../../core/components/Button.jsx'
import { FormTable } from '../../../../../core/components/FormTable/index.tsx'
import { exportJsonToExcel } from '../../../../../core/services/utilitiesService.ts'
import { fetchCompanyHoldingsData } from '../../../services/holdingsService.ts'
import { getAccessToken } from '../../../../../core/auth/authService'
import { store } from '../../../../../state_manager/store.ts'
import { toast } from 'react-toastify'
import { showErrorToast } from '../../../../../core/utils/errorToast.tsx'
import { ensureRowKeys } from '../../../../../core/utils/utilities'
import {
  configToColumns,
  holdingsColumnsConfig,
  transactionSummaryColumnsConfig,
  transactionDetailColumnsConfig,
  prepareDataForExport
} from '../../../../../ui_configs/fund_management/investing/companies/views/holdings_table_columns'

interface HoldingsTabProps {
  recordId?: string | null
  accountName?: string | null
  onRefreshHoldingsReady?: (refreshFn: () => Promise<void>) => void
}

function sanitizeForFilename(s: string): string {
  const sanitized = s.replace(/[/\\:*?"<>|]/g, '-').trim()
  return sanitized || 'Unknown'
}

function buildExportFilename(accountName: string, reportName: string): string {
  const safeAccount = sanitizeForFilename(accountName)
  const safeReport = sanitizeForFilename(reportName)
  const date = dayjs().format('YYYY-MM-DD')
  return `${safeAccount} - ${safeReport} - ${date}.xlsx`
}

export function HoldingsTab({ recordId, accountName, onRefreshHoldingsReady }: HoldingsTabProps) {
  const [loading, setLoading] = useState(false)
  const [exportingHoldings, setExportingHoldings] = useState(false)
  const [exportingSummary, setExportingSummary] = useState(false)
  const [exportingDetail, setExportingDetail] = useState(false)
  const [data, setData] = useState<{
    holdings: (Record<string, unknown> & { key: string })[]
    transactionSummary: (Record<string, unknown> & { key: string })[]
    transactionDetail: (Record<string, unknown> & { key: string })[]
  }>({ holdings: [], transactionSummary: [], transactionDetail: [] })

  const fetchData = useCallback(async () => {
    if (!recordId) return
    setLoading(true)
    try {
      const token = getAccessToken(store.getState().auth)
      const result = await fetchCompanyHoldingsData(recordId, token)
      if (result.error) {
        showErrorToast(result.error)
        setData({ holdings: [], transactionSummary: [], transactionDetail: [] })
      } else if (result.data) {
        const holdings = ensureRowKeys(Array.isArray(result.data.holdings) ? result.data.holdings : [], 'asset')
        const transactionSummary = ensureRowKeys(
          Array.isArray(result.data.transactionSummary) ? result.data.transactionSummary : []
        )
        const transactionDetail = ensureRowKeys(
          Array.isArray(result.data.transactionDetail) ? result.data.transactionDetail : []
        )
        setData({ holdings, transactionSummary, transactionDetail })
      }
    } finally {
      setLoading(false)
    }
  }, [recordId])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  useEffect(() => {
    if (onRefreshHoldingsReady) {
      onRefreshHoldingsReady(fetchData)
    }
  }, [onRefreshHoldingsReady, fetchData])

  const handleExport = useCallback(
    async (
      tableData: (Record<string, unknown> & { key: string })[],
      reportName: string,
      columnConfig: typeof holdingsColumnsConfig,
      setExporting: (v: boolean) => void
    ) => {
      setExporting(true)
      try {
        const token = getAccessToken(store.getState().auth)
        const rows = tableData.map(({ key, ...rest }) => rest)
        const { data, columns, dateColumns } = prepareDataForExport(rows, columnConfig)
        const filename = buildExportFilename(accountName || 'Unknown', reportName)
        const result = await exportJsonToExcel({ data, columns, filename, dateColumns }, token)
        if (result.success) {
          toast.success(`${filename} exported successfully`)
        } else {
          showErrorToast(result.error || 'Export failed')
        }
      } finally {
        setExporting(false)
      }
    },
    [accountName]
  )

  const { openFormInTab } = useTabManagerContext()

  // Record links (Fund / Security / Round / Tranches / Date -> transaction)
  // come from the linkModule/linkIdKey column config; the RPCs emit the
  // matching *__id keys alongside the display names.
  const openRecord = useCallback(
    (module: string, id: string, title: string) => openFormInTab(module, id, title),
    [openFormInTab]
  )
  const holdingsColumns = useMemo(() => configToColumns(holdingsColumnsConfig, openRecord), [openRecord])
  const summaryColumns = useMemo(
    () => configToColumns(transactionSummaryColumnsConfig, openRecord),
    [openRecord]
  )
  const detailColumns = useMemo(
    () => configToColumns(transactionDetailColumnsConfig, openRecord),
    [openRecord]
  )

  const totalRowProps = (record: Record<string, unknown>) => {
    const className = record.funds__name === 'Total' ? 'font-bold bg-gray-50' : undefined
    return className ? { className } : {}
  }

  return (
    <div
      data-debug='company-holdings-tab-container'
      data-component='HoldingsTab'
      className='h-full overflow-y-auto overflow-x-hidden'
    >
      <Row gutter={[16, 16]}>
        <Col span={24}>
          <FormTable
            title='Current Holdings'
            dataSource={data.holdings}
            columns={holdingsColumns}
            loading={loading}
            emptyText='No holdings data'
            size='small'
            pagination={false}
            showHeader={true}
            includeActions={false}
            onRow={(record) => totalRowProps(record)}
            headerActions={
              <Button
                variant='default'
                size='small'
                icon={<FontAwesomeIcon icon={faDownload} />}
                onClick={() => handleExport(data.holdings, 'Current Holdings', holdingsColumnsConfig, setExportingHoldings)}
                loading={exportingHoldings}
                disabled={data.holdings.length === 0}
              >
                Export
              </Button>
            }
          />
        </Col>
        <Col span={24}>
          <FormTable
            title='Transaction Summary'
            dataSource={data.transactionSummary}
            columns={summaryColumns}
            loading={loading}
            emptyText='No transaction summary data'
            size='small'
            pagination={false}
            showHeader={true}
            includeActions={false}
            onRow={(record) => totalRowProps(record)}
            headerActions={
              <Button
                variant='default'
                size='small'
                icon={<FontAwesomeIcon icon={faDownload} />}
                onClick={() => handleExport(data.transactionSummary, 'Transaction Summary', transactionSummaryColumnsConfig, setExportingSummary)}
                loading={exportingSummary}
                disabled={data.transactionSummary.length === 0}
              >
                Export
              </Button>
            }
          />
        </Col>
        <Col span={24}>
          <FormTable
            title='Transaction Details'
            dataSource={data.transactionDetail}
            columns={detailColumns}
            loading={loading}
            emptyText='No transaction detail data'
            size='small'
            pagination={false}
            showHeader={true}
            includeActions={false}
            onRow={(record) => totalRowProps(record)}
            headerActions={
              <Button
                variant='default'
                size='small'
                icon={<FontAwesomeIcon icon={faDownload} />}
                onClick={() => handleExport(data.transactionDetail, 'Transaction Details', transactionDetailColumnsConfig, setExportingDetail)}
                loading={exportingDetail}
                disabled={data.transactionDetail.length === 0}
              >
                Export
              </Button>
            }
          />
        </Col>
      </Row>
    </div>
  )
}
